import { z } from 'zod';
import { NOTIFICATION_KINDS } from '../notifications.ts';
import { timeOfDaySchema } from './planning.ts';

export const notificationKindSchema = z.enum(NOTIFICATION_KINDS);

export const notificationPrefSchema = z.object({
  kind: notificationKindSchema,
  enabled: z.boolean(),
  time: timeOfDaySchema.nullable(),
  dayOffset: z.union([z.literal(0), z.literal(1)]),
  weekday: z.number().int().min(1).max(7).nullable(),
});
export type NotificationPref = z.infer<typeof notificationPrefSchema>;

export const notificationPrefUpdateSchema = notificationPrefSchema.omit({ kind: true }).partial();
export type NotificationPrefUpdate = z.infer<typeof notificationPrefUpdateSchema>;

/** A browser's PushSubscription as `toJSON()` gives it. */
export const pushSubscriptionSchema = z.object({
  endpoint: z.url({ protocol: /^https$/ }).max(2000),
  keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }),
});
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>;

export const unsubscribeSchema = z.object({ endpoint: z.string().max(2000) });

/** `GET /api/notifications`. */
export const notificationSettingsSchema = z.object({
  /** Null when the server has no VAPID keys (push off). */
  vapidPublicKey: z.string().nullable(),
  /** Subscribed devices of this user. */
  devices: z.number().int(),
  prefs: z.array(notificationPrefSchema),
});
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

export const testPushResultSchema = z.object({ sent: z.number().int(), failed: z.number().int() });
export type TestPushResult = z.infer<typeof testPushResultSchema>;

/** What the service worker receives in a push. */
export const pushPayloadSchema = z.object({
  title: z.string(),
  body: z.string(),
  /** Opened on tap (same origin). */
  url: z.string(),
  /** Same tag replaces an older notification. */
  tag: z.string().optional(),
});
export type PushPayload = z.infer<typeof pushPayloadSchema>;

/** Timers ring for up to a day; anything later is a mistake. */
export const TIMER_ALARM_MAX_MS = 24 * 3600 * 1000;

/**
 * `PUT /api/notifications/timers/:id`: push "timer done" to this user's devices at `endsAt`
 * (cooking mode, so a locked phone still rings). Putting it again moves it.
 */
export const timerAlarmSchema = z.object({
  /** Epoch milliseconds. */
  endsAt: z.number().int().positive(),
  label: z.string().trim().min(1).max(200),
  /** A path in the app, opened on tap. */
  url: z
    .string()
    .regex(/^\/(?!\/)/)
    .max(500),
});
export type TimerAlarmInput = z.infer<typeof timerAlarmSchema>;
